import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getDataSchema, getUserCustomDataByLocation } from "../api/data";
import { ExploreSchema, ModelTypes, UserCustomDataPoint } from "../core/global";

const enableCustomData = import.meta.env.VITE_ENABLE_CUSTOM_DATA === 'true'

export interface ExploreLocationParams {
  model: ModelTypes | null,
  provider: string | null,
  geography: string | null,
  version: string | null,
  lat?: number | string | null, 
  lon?: number | string | null, 
  orderId?: string | number | null,
  startDate?: number | null, 
  endDate?: number | null,
  lengthYears?: number | null,
  filename?: string,
}

export interface UpdateExploreDataProps extends ExploreLocationParams {
  source?: string
  preview?: boolean
  cache?: boolean // by default we cache data, but when purchasing data, we only want a quick lookup and don't actually want cache it.
}
export interface GetBinaryDataProps extends UpdateExploreDataProps {
  key?: string
}
interface ExploreContextProps {
  exploreLocationParams: ExploreLocationParams | null
  setExploreLocationParams: React.Dispatch<React.SetStateAction<ExploreLocationParams | null>>;
  isPurchasedPoint: boolean;
  exploreCoords: [number, number] | null;
  setExploreCoords: (val: [number, number] | null) => void;
  exploreDataSchema: ExploreSchema | null;
  exploreError: string[];
  loading: boolean;
  customDataPoints: UserCustomDataPoint[] | null;
  normaliseTimeline: boolean;
  setNormaliseTimeline: React.Dispatch<React.SetStateAction<boolean>>;
  secondaryDataSchema: ExploreSchema | null;
  currentCustomPoint: UserCustomDataPoint | null;
  setCurrentCustomPoint: React.Dispatch<React.SetStateAction<UserCustomDataPoint | null>>;
  linkSecondaryVectors: boolean;
  setLinkSecondaryVectors: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialValue: ExploreContextProps = {
  exploreLocationParams: null,
  setExploreLocationParams: () => {},
  isPurchasedPoint: false,
  exploreCoords: null,
  setExploreCoords: () => {},
  exploreDataSchema: null,
  exploreError: [],
  loading: false,
  customDataPoints: null,
  normaliseTimeline: false,
  setNormaliseTimeline: () => {},
  secondaryDataSchema: null,
  currentCustomPoint: null,
  setCurrentCustomPoint: () => {},
  linkSecondaryVectors: true,
  setLinkSecondaryVectors: () => {},
};

interface ExploreProviderProps {
  children?: React.ReactNode;
}

export const ExploreContext = createContext<ExploreContextProps>(initialValue);

export const ExploreProvider = ({ children }: ExploreProviderProps) => {
  const [normaliseTimeline, setNormaliseTimeline] = useState<boolean>(initialValue.normaliseTimeline);
  const [linkSecondaryVectors, setLinkSecondaryVectors] = useState<boolean>(initialValue.linkSecondaryVectors);
  const [customDataPoints, setCustomDataPoints] = useState(initialValue.customDataPoints);
  const [secondaryDataSchema, setSecondaryDataSchema] = useState<ExploreSchema | null>(initialValue.secondaryDataSchema);
  const [currentCustomPoint, setCurrentCustomPoint] = useState(initialValue.currentCustomPoint);

  const [exploreLocationParams, setExploreLocationParams] = useState<ExploreLocationParams | null>(null);
  const [exploreCoords, _setExploreCoords] = useState(
    initialValue.exploreCoords
  );
  const [loading, setLoading] = useState(initialValue.loading);
  const [exploreError, setExploreError] = useState(initialValue.exploreError);
  
  const [exploreDataSchema, setExploreDataSchema] = useState(
    initialValue.exploreDataSchema
  );

  useEffect(() => {
    if (currentCustomPoint !== null 
      && (customDataPoints !== null && customDataPoints?.length > 0)
    ) {
      // Load schema

        const execute = async (params: UserCustomDataPoint) => {
          const schemaParams: UpdateExploreDataProps = {
            ...params,
            orderId: params?.container || ''
          }
          const result = await getDataSchema({
            ...schemaParams
          });
          const { response, error, message } = result;
  
          const { description, timeline, vectors } = response || {};
  
          if (error !== 200 && message) {
            setExploreError((m) => [...m, `Error loading data schema for custom data.`]);
          } else if (
            error === 200 && description && timeline && vectors && vectors.length > 0
          ) {
            const schema = {
              description,
              timeline,
              vectors,
            };
            setSecondaryDataSchema(schema);
          }
        }
        execute({...currentCustomPoint})
    } else {
      setSecondaryDataSchema(null)
    }
  }, [JSON.stringify(currentCustomPoint), JSON.stringify(customDataPoints)])

  const isS3StoredPoint = useMemo(() => {
    if ( exploreLocationParams?.orderId && exploreLocationParams?.filename ) {
      return true
    }
    return !!(exploreLocationParams?.lengthYears && exploreLocationParams.lengthYears > 1) 
    || !!exploreLocationParams?.startDate 
    || !!exploreLocationParams?.endDate;

  }, [JSON.stringify(exploreLocationParams)])

  const setExploreCoords = useCallback((val: [number, number] | null) => {
    _setExploreCoords(val)
  }, [isS3StoredPoint, exploreCoords]);

  // HACK: fix this
  const getSchemaParams = useCallback((params: ExploreLocationParams | null, key?: string | null): (GetBinaryDataProps | null) => {
    if (!params) {
      return null;
    }
    if (key) {
      return {
        ...params, key, preview: !isS3StoredPoint, source: isS3StoredPoint ? "s3": "azure"
      }
    }
    return {
      ...params, preview: !isS3StoredPoint, source: isS3StoredPoint ? "s3": "azure"
    }
  }, [isS3StoredPoint])

  const loadCustomData = useCallback( async (params: any) => {
    const {lat, lon} = params

    const result = await getUserCustomDataByLocation({
      lat,
      lon
    });

    const { response, error, message } = result;

    const { rows } = response || {};

    if (error !== 200 && message) {
      // setExploreError((m) => [`Error loading custom data.`]);
      // do not set error, fail silently as this is not critical and we don't want to block user from seeing explore data if their custom data fails to load for some reason
    } else {
      setCustomDataPoints(rows || null);
    }
  }, [])

  const getExploreDataSchema = useCallback(() => {
    const execute = async (schemaParams: UpdateExploreDataProps) => {
      setLoading(true);
      // Call for getting schema always resets the data object
      setExploreDataSchema(initialValue.exploreDataSchema);
      setExploreError(initialValue.exploreError);
      
      const shouldLoadCustomData = !(
        typeof exploreLocationParams?.orderId === "string" &&
        typeof exploreLocationParams?.filename === "number"        
      )

      if (shouldLoadCustomData && enableCustomData) {
        await loadCustomData({...exploreLocationParams});
      }
      const result = await getDataSchema({
        ...schemaParams
      });
      
      const { response, error, message } = result || {};

      const { description, timeline, vectors } = response || {};

      if (error !== 200 && message) {
        setExploreError((m) => [`Error loading data schema.`]);
      } else if (
        error === 200 && description && timeline && vectors && vectors.length > 0
      ) {
        const schema = {
          description,
          timeline,
          vectors,
        };
        setExploreDataSchema(schema);
      } else {
        setExploreError((m) => [`Error loading data schema - missing data.`]);
      }
      setLoading(false);
    };

    const schemaParams = getSchemaParams(exploreLocationParams)

    if (schemaParams) {
      execute(schemaParams);
    }
  }, [exploreLocationParams]);

  useEffect(() => {
    if (!exploreLocationParams) {
      setExploreDataSchema(null)
    } else {
      const {model, lat, lon, filename, orderId} = exploreLocationParams;
    
      if (filename && orderId) {
        getExploreDataSchema();
        return;
      }
      if (lat === null || lat === undefined || lon == null || lon === undefined || typeof model !== "string") {
        return;
      }
      getExploreDataSchema();
    }

    }, [JSON.stringify(exploreLocationParams), isS3StoredPoint]
  );


  return (
    <ExploreContext.Provider
      value={{
        isPurchasedPoint: isS3StoredPoint,
        exploreDataSchema,
        exploreCoords,
        setExploreCoords,
        exploreError,
        loading,
        exploreLocationParams,
        setExploreLocationParams,
        customDataPoints,
        secondaryDataSchema,
        normaliseTimeline, 
        setNormaliseTimeline,
        currentCustomPoint, 
        setCurrentCustomPoint,
        linkSecondaryVectors,
        setLinkSecondaryVectors,
      }}
    >
      {children}
    </ExploreContext.Provider>
  );
};
